import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Loader2, Pencil, Grid as GridIcon, Edit3 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { v: 'present',  l: 'P', cls: 'bg-emerald-100 text-emerald-700' },
  { v: 'absent',   l: 'A', cls: 'bg-red-100 text-red-700' },
  { v: 'leave',    l: 'L', cls: 'bg-blue-100 text-blue-700' },
  { v: 'half_day', l: 'HD', cls: 'bg-amber-100 text-amber-700' },
  { v: 'lop',      l: 'LOP', cls: 'bg-rose-100 text-rose-700' },
  { v: 'weekly_off', l: 'WO', cls: 'bg-slate-100 text-slate-600' },
  { v: 'holiday',  l: 'H', cls: 'bg-purple-100 text-purple-700' },
  { v: 'on_duty',  l: 'OD', cls: 'bg-cyan-100 text-cyan-700' },
  { v: 'work_from_home', l: 'WFH', cls: 'bg-indigo-100 text-indigo-700' },
];

const statusBadge = (s) => STATUS_OPTIONS.find(x => x.v === s) || { l: '—', cls: 'bg-slate-50 text-slate-400' };

export default function InternalAttendance() {
  const [tab, setTab] = useState('entry');     // entry | grid
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        const cs = Array.isArray(data) ? data : (data.companies || []);
        setCompanies(cs);
        if (cs.length === 1) setCompanyId(cs[0].id);
      } catch { /* */ }
    })();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="internal-attendance-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Pencil className="text-blue-600" size={28} /> Internal Attendance
        </h1>
        <p className="text-gray-600 mt-1">HR direct entry / month grid editor / single-cell override.</p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select value={companyId} onChange={e => setCompanyId(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
          <option value="">— pick a company —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="ml-auto bg-slate-100 rounded-lg p-1 flex">
          <button onClick={() => setTab('entry')}
            data-testid="tab-entry"
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === 'entry' ? 'bg-white shadow' : 'text-slate-600'}`}>
            <Edit3 size={14} className="inline mr-1" /> Single Entry / Override
          </button>
          <button onClick={() => setTab('grid')}
            data-testid="tab-grid"
            className={`px-4 py-1.5 rounded-md text-sm font-medium ${tab === 'grid' ? 'bg-white shadow' : 'text-slate-600'}`}>
            <GridIcon size={14} className="inline mr-1" /> Month Grid Editor
          </button>
        </div>
      </div>

      {tab === 'entry' ? <SingleEntry companyId={companyId} />
        : <MonthGrid companyId={companyId} />}
    </div>
  );
}

function SingleEntry({ companyId }) {
  const [form, setForm] = useState({
    employee_code: '', attendance_date: new Date().toISOString().slice(0, 10),
    attendance_status: 'present', in_time: '', out_time: '', remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!companyId) { toast.error('Pick a company first'); return; }
    if (!form.employee_code) { toast.error('Employee code required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/attendance/v2/internal/entry`,
        { ...form, company_id: companyId }, { withCredentials: true });
      toast.success('Saved');
      setForm({ ...form, employee_code: '', remarks: '', in_time: '', out_time: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-2xl">
      <h3 className="font-semibold mb-3">Single Entry / Calendar Override</h3>
      <p className="text-xs text-slate-500 mb-4">Use this for one-off entries or to override a single date/employee.</p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Employee Code *</label>
          <input value={form.employee_code} onChange={e => setForm({ ...form, employee_code: e.target.value })}
            data-testid="entry-employee-code"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm" placeholder="EMP001" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Date *</label>
          <input type="date" value={form.attendance_date}
            onChange={e => setForm({ ...form, attendance_date: e.target.value })}
            data-testid="entry-date"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Status *</label>
          <select value={form.attendance_status} onChange={e => setForm({ ...form, attendance_status: e.target.value })}
            data-testid="entry-status"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm">
            {STATUS_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.v.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">In / Out (optional)</label>
          <div className="flex gap-2 mt-1">
            <input type="time" value={form.in_time} onChange={e => setForm({ ...form, in_time: e.target.value })}
              className="flex-1 border border-slate-300 rounded-lg px-2 py-2 text-sm" />
            <input type="time" value={form.out_time} onChange={e => setForm({ ...form, out_time: e.target.value })}
              className="flex-1 border border-slate-300 rounded-lg px-2 py-2 text-sm" />
          </div>
        </div>
        <div className="col-span-2">
          <label className="text-xs font-medium text-slate-600">Remarks</label>
          <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 mt-1 text-sm"
            placeholder="e.g. Override — biometric was offline" />
        </div>
      </div>

      <button onClick={submit} disabled={saving}
        data-testid="entry-save"
        className="mt-4 w-full bg-blue-600 text-white py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-1">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save Entry / Override
      </button>
    </div>
  );
}

function MonthGrid({ companyId }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [grid, setGrid] = useState(null);
  const [edits, setEdits] = useState({});  // {`${eid}|${date}`: status}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!companyId) { toast.error('Pick a company first'); return; }
    setLoading(true); setEdits({});
    try {
      const { data } = await axios.get(
        `${API_URL}/attendance/v2/internal/grid?company_id=${companyId}&month=${month}`,
        { withCredentials: true });
      setGrid(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  };

  const setCell = (eid, d, status) => {
    setEdits(prev => ({ ...prev, [`${eid}|${d}`]: status }));
  };

  const save = async () => {
    const entries = Object.entries(edits).map(([k, status]) => {
      const [employee_id, attendance_date] = k.split('|');
      return { employee_id, attendance_date, status };
    });
    if (entries.length === 0) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/attendance/v2/internal/grid`,
        { company_id: companyId, month, entries }, { withCredentials: true });
      toast.success(`Saved ${data.saved} cells${data.errors?.length ? ` (${data.errors.length} errors)` : ''}`);
      setEdits({});
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const cellStatus = (eid, d, original) => edits[`${eid}|${d}`] ?? original;
  const totalEdits = Object.keys(edits).length;

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-3 flex items-center gap-3">
        <input type="month" value={month} onChange={e => setMonth(e.target.value)}
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        <button onClick={load} disabled={loading || !companyId}
          data-testid="grid-load"
          className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50 flex items-center gap-1">
          {loading ? <Loader2 size={14} className="animate-spin" /> : null} Load Grid
        </button>
        <span className="ml-auto text-xs text-slate-500">
          Tip: click a cell, then choose status. Edited cells appear with a blue border.
        </span>
        {totalEdits > 0 && (
          <button onClick={save} disabled={saving}
            data-testid="grid-save"
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium flex items-center gap-1">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save {totalEdits} Change{totalEdits === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {grid && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="grid-table">
          <table className="text-xs border-collapse min-w-max">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="text-left px-2 py-2 sticky left-0 bg-white z-20 border-r border-slate-200">Employee</th>
                {grid.days.map(d => {
                  const dt = new Date(d);
                  const wd = dt.toLocaleDateString('en-US', { weekday: 'short' })[0];
                  return (
                    <th key={d} className="px-1 py-2 text-center font-mono text-slate-500 min-w-[36px] border-l border-slate-100">
                      <div>{d.slice(8)}</div>
                      <div className="text-[10px] text-slate-400">{wd}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {grid.employees.map(e => (
                <tr key={e.employee_id} className="border-t border-slate-100">
                  <td className="px-2 py-1 sticky left-0 bg-white z-10 border-r border-slate-200 whitespace-nowrap">
                    <div className="font-mono text-blue-700 text-[11px]">{e.employee_code}</div>
                    <div className="text-slate-700">{e.name}</div>
                  </td>
                  {grid.days.map(d => {
                    const original = e.cells[d];
                    const cur = cellStatus(e.employee_id, d, original);
                    const isEdited = (`${e.employee_id}|${d}`) in edits;
                    const b = statusBadge(cur);
                    return (
                      <td key={d} className={`p-0 border-l border-slate-100 ${isEdited ? 'ring-2 ring-blue-400 ring-inset' : ''}`}>
                        <select value={cur || ''} onChange={ev => setCell(e.employee_id, d, ev.target.value)}
                          data-testid={`cell-${e.employee_code}-${d}`}
                          className={`w-full text-[10px] font-bold text-center px-1 py-1.5 ${b.cls} cursor-pointer outline-none`}
                          style={{ appearance: 'none', textAlignLast: 'center' }}>
                          <option value="">—</option>
                          {STATUS_OPTIONS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!grid && !loading && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
          Pick a month and click <b>Load Grid</b> to start editing.
        </div>
      )}
    </div>
  );
}
